import { put, select, call, cancelled, takeLatest } from 'redux-saga/effects';
import { EffectR } from 'types/utils';
import log from 'loglevel';
import { api } from 'api';
import { getEnv } from 'common/selectors';
import { getProductId } from 'features/product-info';
import { getRegionId } from 'features/page-model/ducks/get-model';
import { FETCH_TRUST_DOCUMENTS } from './actions';
import { fetchTrustDocuments } from './api';
import { TrustDocumentsResponse } from '../types';
import { getLang } from 'utils';
import { getTrustDocumentsData } from './selector';

interface ApiErrorResponse {
  status: number;
  statusText?: string;
}

function isErrorResponse(response: unknown): response is ApiErrorResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'status' in response &&
    typeof (response as ApiErrorResponse).status === 'number' &&
    (response as ApiErrorResponse).status >= 400
  );
}

export function* doFetchTrustDocuments() {
  const cached: TrustDocumentsResponse | null = yield select(getTrustDocumentsData);
  if (cached) {
    yield put(FETCH_TRUST_DOCUMENTS.success(cached));
    return;
  }

  const productId: string = yield select(getProductId);
  const regionId: string = yield select(getRegionId);
  const lang: string = yield select(getLang);
  const { requestID } = yield select(getEnv);

  if (!productId || !regionId) {
    yield put(FETCH_TRUST_DOCUMENTS.failure(new Error('trustDocuments: productId or regionId is missing')));
    return;
  }

  const abortController = (yield call([api, 'abortController'])) as EffectR.Call<
    (typeof api)['abortController']
  >;

  try {
    log.info('fetchTrustDocuments requested', { requestID, timestamp: Date.now() });

    const response = (yield call(
      fetchTrustDocuments,
      { productId, regionId, lang },
      { cancelToken: abortController.token },
    )) as TrustDocumentsResponse | ApiErrorResponse;

    if (isErrorResponse(response)) {
      throw new Error(`API Error ${response.status}: ${response.statusText || 'Unknown error'}`);
    }

    yield put(FETCH_TRUST_DOCUMENTS.success(response));
    log.info('fetchTrustDocuments loaded', { requestID, timestamp: Date.now() });
  } catch (e) {
    const error = e instanceof Error ? e : new Error('Unknown error');
    yield put(FETCH_TRUST_DOCUMENTS.failure(error));
    log.error('fetchTrustDocuments error', { requestID, timestamp: Date.now(), error: error.message });
  } finally {
    if ((yield cancelled()) as boolean) {
      abortController.cancel();
      yield put(FETCH_TRUST_DOCUMENTS.cancel());
      log.warn('fetchTrustDocuments cancelled', { requestID, timestamp: Date.now() });
    }
  }
}

export function* trustDocumentsSaga() {
  yield takeLatest(FETCH_TRUST_DOCUMENTS.request, doFetchTrustDocuments);
}
